import React from 'react';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { formatCompactInteger, toRoundedInteger } from '../utils/numberFormat';
import { t } from '../i18n';
import './DailyTrendChart.css';

function formatPracticeTime(seconds = 0) {
  const unit = (key, count) => t(`accountActivity.stats.${key}Value`, undefined, { count });
  const safeSeconds = Math.max(0, Number(seconds) || 0);
  const minutes = Math.floor(safeSeconds / 60);
  const remainingSeconds = safeSeconds % 60;

  if (minutes <= 0) return unit('seconds', remainingSeconds);
  if (remainingSeconds === 0) return unit('minutes', minutes);
  return `${unit('minutes', minutes)} ${unit('seconds', remainingSeconds)}`;
}

function CustomDot({ cx, cy, payload }) {
  if (!payload || payload.practiceMinutes == null) return null;
  return <circle cx={cx} cy={cy} r={5} fill="var(--color-success)" stroke="var(--color-surface)" strokeWidth={1} />;
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const time = payload.find((p) => p.dataKey === 'practiceMinutes');
  const score = payload.find((p) => p.dataKey === 'score');

  return (
    <div className="trend-tooltip">
      <div className="trend-tooltip-date">{label}</div>
      {time && <div style={{ color: 'var(--color-success)' }}>{t('accountActivity.stats.practiceTime')}: {formatPracticeTime(time.payload.practiceSeconds)}</div>}
      {score && <div style={{ color: 'var(--color-primary)' }}>{t('accountActivity.stats.score')}: {formatCompactInteger(score.value)}</div>}
    </div>
  );
}

function DailyTrendChart({ title = t('accountActivity.calendar.dailyTrend'), data = [], emptyMessage = t('accountActivity.calendar.empty') }) {
  return (
    <div className="daily-trend-section">
      <h3 className="daily-trend-section-title">{title}</h3>
      {data.length === 0 ? (
        <p className="daily-trend-no-data">{emptyMessage}</p>
      ) : (
        <ResponsiveContainer width="100%" height={180}>
          <ComposedChart data={data} margin={{ top: 8, right: 0, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-line)" />
            <XAxis dataKey="date" tick={{ fill: 'var(--color-muted)', fontSize: 12 }} />
            <YAxis
              yAxisId="time"
              domain={[0, 'dataMax']}
              tick={{ fill: 'var(--color-muted)', fontSize: 12 }}
              tickFormatter={toRoundedInteger}
              label={{ value: t('accountActivity.stats.minutes'), angle: -90, position: 'insideLeft', fill: 'var(--color-muted)', fontSize: 12 }}
            />
            <YAxis
              yAxisId="score"
              orientation="right"
              tick={{ fill: 'var(--color-muted)', fontSize: 12 }}
              tickFormatter={formatCompactInteger}
              label={{ value: t('accountActivity.stats.score'), angle: 90, position: 'insideRight', fill: 'var(--color-muted)', fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ color: 'var(--color-muted)', fontSize: 13 }} />
            <Bar yAxisId="score" dataKey="score" name={t('accountActivity.stats.score')} fill="var(--color-primary)" opacity={0.74} radius={[3, 3, 0, 0]} />
            <Line
              yAxisId="time"
              dataKey="practiceMinutes"
              name={t('accountActivity.stats.practiceTime')}
              stroke="var(--color-success)"
              strokeWidth={2}
              dot={<CustomDot />}
              activeDot={{ r: 7 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

export default DailyTrendChart;
